import type { Question } from "@/types";

/**
 * UI Elements interface for feedback system
 */
interface FeedbackSystemElements {
  feedback: HTMLElement;
  answerInput: HTMLInputElement;
  checkBtn: HTMLButtonElement;
  showAnswerBtn: HTMLButtonElement;
}

/**
 * Example data structure from verb data
 */
interface ExampleData {
  explanation: string;
  examples: Array<{
    german: string;
    english: string;
    danish: string;
  }>;
}

/**
 * Feedback system for showing answer results
 */
export class FeedbackSystem {
  private elements: FeedbackSystemElements;

  constructor(elements: FeedbackSystemElements) {
    this.elements = elements;
  }

  /**
   * Show feedback for an answer
   */
  show(isCorrect: boolean, message: string, question?: Question): void {
    const feedback = this.elements.feedback;

    let feedbackHTML = `
      <div class="feedback-main ${isCorrect ? "correct" : "incorrect"}">
        ${message}
      </div>
    `;

    // Only add examples if we have a valid question
    if (question?.tense && question?.pronoun) {
      // Create feedback content with examples
      const examples = (window as any).verbData?.getExamples(
        question.tense,
        question.pronoun
      );

      if (examples) {
        feedbackHTML += this.createExamplesHTML(examples);
      }
    }

    feedback.innerHTML = feedbackHTML;
    feedback.style.display = "block";

    // Disable inputs
    this.disableInputs();
  }

  /**
   * Create examples HTML
   */
  private createExamplesHTML(examples: ExampleData): string {
    let html = `
      <div class="feedback-explanation">
        <strong>Explanation:</strong> ${examples.explanation}
      </div>
      <div class="feedback-examples">
        <strong>Examples:</strong>
    `;

    // Show up to 2 examples
    const exampleList = examples.examples.slice(0, 2);
    exampleList.forEach((example) => {
      html += `
        <div class="example-set">
          <div class="example-german">${example.german}</div>
          <div class="example-english">${example.english}</div>
          <div class="example-danish">${example.danish}</div>
        </div>
      `;
    });

    html += `</div>`;
    return html;
  }

  /**
   * Disable input controls after answer
   */
  private disableInputs(): void {
    this.elements.answerInput.disabled = true;
    this.elements.checkBtn.disabled = true;
    this.elements.showAnswerBtn.disabled = true;
  }

  /**
   * Hide feedback
   */
  hide(): void {
    this.elements.feedback.style.display = "none";
  }

  /**
   * Enable inputs for new question
   */
  enableInputs(): void {
    this.elements.answerInput.disabled = false;
    this.elements.checkBtn.disabled = false;
    this.elements.showAnswerBtn.disabled = false;
  }

  /**
   * Clear feedback content and hide
   */
  clear(): void {
    this.elements.feedback.innerHTML = "";
    this.elements.feedback.style.display = "none";
    this.enableInputs();
  }
}

// Export for global usage
export default FeedbackSystem;

// Make available globally for backward compatibility
declare global {
  interface Window {
    FeedbackSystem: any;
  }
}

if (typeof window !== "undefined") {
  window.FeedbackSystem = FeedbackSystem;
}
